import { useContext, useRef, useState } from "react";
import Form, { FormData, FormDefinition } from "../../Components/Form/Form";
import { AuthContext, AuthContextType } from "../../utilities/customHooks";
import { ToastContext, ToastContextType } from "../../utilities/customHooks";
import { useSuspenseQuery } from "@tanstack/react-query";
import { queryKeys } from "../../utilities/queryKeys";
import { getFirmProfile } from "../../services/serviceProvider";
import { useUpdateFirmProfile } from "../../services/serviceProviderMutations";
import { requestEmailChange } from "../../services/userService";
import { description_validator, firm_name_validator, url_validator, address_validator, email_validator, multiselect_validator, basic_text_validator, phone_validator } from "../../utilities/formFieldValidators";
import { INDUSTRY_OPTIONS, parseIndustries, serializeIndustries } from "../../utilities/industryOptions";
import { LANGUAGE_OPTIONS, parseLanguages, serializeLanguages } from "../../utilities/languageOptions";
import { joinContactValues } from "../../utilities/helperFunctions";
import { PHONE_COMPLETE_REGEX, normalizePhone } from "@eip/shared/utils/phone";
import Tooltip from "../../Components/Tooltip";
import Button from "../../Components/Button";
import { Address } from "../../Components/Form/AddressFields";
import DashboardPageLayout from "../../Components/DashboardPageLayout";
import StickyPageHeader from "../../Components/StickyPageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../../Components/ui/card";
import { Input } from "../../Components/ui/input";
import { Label } from "../../Components/ui/label";
import { Badge } from "../../Components/ui/badge";
import { Mail, Shield } from "lucide-react";
import { getRegionCapabilities } from "../../utilities/regionCapabilities";

interface FirmProfileProps {
  mode?: "edit" | "onboarding";
}

function FirmProfile({ mode = "edit" }: FirmProfileProps) {
  const showToast = (useContext(ToastContext) as ToastContextType).showToast;
  const { refreshUserInfo, userEmail, pendingEmail, userRegion } = useContext(AuthContext) as AuthContextType;
  const updateFirmProfileMutation = useUpdateFirmProfile();
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailUpdateInProgress, setEmailUpdateInProgress] = useState(false);
  const [hstNumber, setHstNumber] = useState("");
  const [hstError, setHstError] = useState("");
  const [pstNumber, setPstNumber] = useState("");
  const [pstError, setPstError] = useState("");
  const formStateRef = useRef<FormData>({});
  const submitRef = useRef<(() => void) | null>(null);

  const isOnboarding = mode === "onboarding";

  // Region-driven: NF (split-tax) shows both GST# + PST#; others show HST# only.
  const taxConfig = getRegionCapabilities(userRegion).taxConfig;
  const showPstField = taxConfig.type === "split";

  const { data: firmData } = useSuspenseQuery({
    queryKey: queryKeys.providers.firmProfile(),
    queryFn: () => getFirmProfile(),
  });

  const firm = firmData.firm;

  // Legacy docs hold array-valued contact/contactEmail (server model is Mixed —
  // review R24). Normalize for the single-value inputs: contact names join into
  // one display string ("Ann, Bob"); contactEmail keeps the FIRST entry, because
  // the input holds exactly one address and legacy extras would fail the email
  // validator and block every save. Non-destructive at rest: the normalized
  // shape only persists when the SP saves this form.
  const normalizedContact = joinContactValues(firm.contact);
  const normalizedContactEmail = Array.isArray(firm.contactEmail)
    ? (firm.contactEmail.find(Boolean) ?? "")
    : (firm.contactEmail || "");

  // Tel inputs store raw digits (shared rule in @eip/shared/utils/phone —
  // review R23; normalizePhone moved there, C30). Legacy values may be
  // formatted ("(555) 123-4567"): strip to digits so the prefill passes the
  // shared complete-number rule. Anything that still isn't a plausible number
  // is left as-is and only blocks saving if the SP edits the field (below).
  const normalizedContactPhone = normalizePhone(firm.contactPhone);

  // C8: an untouched legacy phone that normalizePhone couldn't rescue
  // ("555-1234 ext. 204", "+44 20 7946 0958", …) must not block unrelated
  // edits. InputField's tel validate closure tests validator.pattern against
  // the raw field value, so widen the pattern to ALSO accept the exact loaded
  // legacy value; any CHANGED value still has to be a complete number. The
  // unchanged value is change-gated out of the payload in handleSave, so the
  // lenient branch never reaches the server.
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const contactPhoneValidator =
    normalizedContactPhone && !PHONE_COMPLETE_REGEX.test(normalizedContactPhone)
      ? {
          ...phone_validator,
          required: false,
          pattern: {
            value: new RegExp(`${PHONE_COMPLETE_REGEX.source}|^${escapeRegExp(normalizedContactPhone)}$`),
            message: phone_validator.pattern.message,
          },
        }
      : { ...phone_validator, required: false };

  // Baselines for the industries/languages round-trip (review R18): the stored
  // string is only rewritten when the SP actually changed the selection, so an
  // untouched legacy value stays byte-identical at rest.
  const initialIndustries = parseIndustries(firm.industriesOfFocus);
  const initialLanguages = parseLanguages(firm.languages);

  // Initialize form state ref from firm data (safe — runs after loading guard)
  if (Object.keys(formStateRef.current).length === 0) {
    formStateRef.current = {
      name: firm.name || "",
      description: firm.description || "",
      websiteURL: firm.websiteURL || "",
      industriesOfFocus: initialIndustries,
      languages: initialLanguages,
      contact: normalizedContact,
      contactEmail: normalizedContactEmail || userEmail || "",
      contactPhone: normalizedContactPhone,
      address: firm.address || null,
    };
    setHstNumber(firm.hstNumber || "");
    setPstNumber(firm.pstNumber || "");
  }

  const firmProfileFormDefinition: FormDefinition = {
    name: {
      label: "Firm Name (Visible to Participants)",
      frLabel: "Nom de la firme (visible par les participants)",
      inputType: "text",
      validator: firm_name_validator,
      placeholder: "Service provider firm name",
      defaultValue: firm.name || "",
    },
    description: {
      label: "Description (Visible to Participants)",
      frLabel: "Description (visible par les participants)",
      inputType: "textarea",
      validator: description_validator,
      placeholder: "Describe your firm's services and expertise",
      defaultValue: firm.description || "",
    },
    websiteURL: {
      label: "Website (Visible to Participants)",
      frLabel: "URL du site Web (visible par les participants)",
      inputType: "text",
      validator: url_validator,
      placeholder: "https://www.example.com",
      defaultValue: firm.websiteURL || "",
    },
    industriesOfFocus: {
      label: "Industries of Focus - please include a list of technologies the Firm has expertise working with (Visible to Participants)",
      frLabel: "Industries ciblées - veuillez inclure une liste des technologies pour lesquelles la firme possède une expertise (visible par les participants)",
      inputType: "multiSelect",
      validator: multiselect_validator,
      additionalData: { selectionData: INDUSTRY_OPTIONS },
      defaultValue: initialIndustries,
    },
    languages: {
      label: "Languages Offered (Visible to Participants)",
      frLabel: "Langues offertes (visible par les participants)",
      inputType: "multiSelect",
      validator: multiselect_validator,
      additionalData: { selectionData: LANGUAGE_OPTIONS },
      defaultValue: initialLanguages,
    },
    address: {
      label: "Firm Address (Visible to Participants)",
      frLabel: "Adresse de la firme (visible par les participants)",
      inputType: "address",
      validator: address_validator,
      placeholder: "Start typing address...",
      defaultValue: firm.address || null,
    },
    contact: {
      label: "Contact Person (Visible to Participants)",
      frLabel: "Personne-ressource (visible par les participants)",
      inputType: "text",
      validator: { ...basic_text_validator, required: false },
      placeholder: "Contact person name",
      defaultValue: normalizedContact,
    },
    contactEmail: {
      label: "Contact Email (Visible to Participants)",
      frLabel: "Courriel de contact (visible par les participants)",
      inputType: "text",
      validator: { ...email_validator, required: false },
      placeholder: "contact@example.com",
      defaultValue: normalizedContactEmail || userEmail || "",
    },
    contactPhone: {
      // Shared phone rule (review R23): tel input stores raw digits and the
      // validate closure in InputField enforces validator.pattern — a bare
      // `pattern` on a tel input would be a silent no-op (not RHF-wired).
      // contactPhoneValidator additionally tolerates an untouched invalid
      // legacy prefill (C8, see above).
      label: "Contact Phone (Visible to Participants)",
      frLabel: "Téléphone de contact (visible par les participants)",
      inputType: "text",
      validator: contactPhoneValidator,
      defaultValue: normalizedContactPhone,
    },
  };

  const handleSave = (updatedForm: FormData) => {
    if (updateFirmProfileMutation.isPending) return;

    // R18: only rewrite the stored industries/languages strings when the SP
    // actually changed the selection. An untouched legacy value (arbitrary
    // free text) is omitted from the payload and stays byte-identical at
    // rest — the parse/serialize round-trip itself preserves unmatched
    // tokens, so even a touched field keeps legacy entries.
    const industriesOut = serializeIndustries(updatedForm.industriesOfFocus as string[]);
    const languagesOut = serializeLanguages(updatedForm.languages as string[]);
    const industriesChanged = industriesOut !== serializeIndustries(initialIndustries);
    const languagesChanged = languagesOut !== serializeLanguages(initialLanguages);

    // C8: same change-gating for contactPhone — an untouched value (possibly
    // invalid legacy free text the strict server validator would reject) is
    // omitted from the payload and stays byte-identical at rest; a changed
    // value has already passed the strict complete-number validation.
    const contactPhoneOut = updatedForm.contactPhone as string;
    const contactPhoneChanged = contactPhoneOut !== normalizedContactPhone;

    updateFirmProfileMutation.mutate(
      {
        name: updatedForm.name as string,
        description: updatedForm.description as string,
        websiteURL: updatedForm.websiteURL as string,
        ...(industriesChanged && { industriesOfFocus: industriesOut }),
        ...(languagesChanged && { languages: languagesOut }),
        address: updatedForm.address as Address,
        contact: updatedForm.contact as string,
        contactEmail: updatedForm.contactEmail as string,
        ...(contactPhoneChanged && { contactPhone: contactPhoneOut }),
        hstNumber,
        ...(showPstField && { pstNumber }),
      },
      {
        onSuccess: async () => {
          showToast({
            toastType: "success",
            message: isOnboarding
              ? "Firm profile completed successfully!"
              : "Firm profile updated successfully.",
          });
          if (isOnboarding) await refreshUserInfo();
        },
      }
    );
  };

  const HST_PATTERN = /^\d{9}RT\d{4}$/;
  const PST_PATTERN = /^\d{6}-?\d$/;

  const validateHst = (value: string): string => {
    if (!value) return "";
    if (!HST_PATTERN.test(value)) return "Must be 15 characters: 9 digits + RT + 4 digits (e.g., 123456789RT0001)";
    return "";
  };

  const validatePst = (value: string): string => {
    if (!value) return "";
    if (!PST_PATTERN.test(value)) return "PST/RST number must be 7 digits (format: xxxxxx-x)";
    return "";
  };

  const validateEmail = (value: string): string => {
    if (!value) return "Please enter an email address.";
    if (!email_validator.pattern.value.test(value)) return "Please enter a valid email address.";
    if (value.toLowerCase() === userEmail?.toLowerCase()) return "This is already your current email.";
    return "";
  };

  const handleTaxNumbersUpdate = () => {
    if (updateFirmProfileMutation.isPending) return;

    const hstErr = validateHst(hstNumber);
    if (hstErr) { setHstError(hstErr); return; }
    const pstErr = showPstField ? validatePst(pstNumber) : "";
    if (pstErr) { setPstError(pstErr); return; }

    const hstChanged = hstNumber !== (firm.hstNumber || "");
    const pstChanged = showPstField && pstNumber !== (firm.pstNumber || "");
    if (!hstChanged && !pstChanged) {
      showToast({ toastType: "failure", message: "No changes to save." });
      return;
    }

    const payload: { hstNumber?: string; pstNumber?: string } = {};
    if (hstChanged) payload.hstNumber = hstNumber;
    if (pstChanged) payload.pstNumber = pstNumber;

    updateFirmProfileMutation.mutate(
      payload,
      {
        onSuccess: () => {
          showToast({ toastType: "success", message: "Tax numbers updated successfully." });
        },
      }
    );
  };

  const handleEmailUpdate = async () => {
    const error = validateEmail(newEmail);
    if (error) { setEmailError(error); return; }

    setEmailUpdateInProgress(true);
    const response = await requestEmailChange(newEmail);
    setEmailUpdateInProgress(false);

    if (response.success) {
      showToast({ toastType: "success", message: response.message || "Verification email sent!" });
      setNewEmail("");
      await refreshUserInfo();
    } else {
      showToast({ toastType: "failure", message: response.message || "Failed to update email." });
    }
  };

  const handleResendVerification = async () => {
    if (!pendingEmail) return;

    setEmailUpdateInProgress(true);
    const response = await requestEmailChange(pendingEmail);
    setEmailUpdateInProgress(false);

    if (response.success) {
      showToast({ toastType: "success", message: "Verification email resent!" });
    } else {
      showToast({ toastType: "failure", message: response.message || "Failed to resend email." });
    }
  };

  const handleFormDataChange = (data: FormData) => {
    formStateRef.current = data;
  };

  const handleHeaderSave = () => {
    if (submitRef.current) {
      submitRef.current();
    }
  };


  return (
    <>
      <StickyPageHeader
        title={isOnboarding ? "Complete Your Firm Profile" : "Firm Profile"}
        actions={
          <Button
            buttonText={isOnboarding ? "Complete Firm Profile" : "Save Changes"}
            buttonType="primary"
            actionHandler={handleHeaderSave}
            disabled={updateFirmProfileMutation.isPending}
          />
        }
      />
      <DashboardPageLayout>
        {isOnboarding && (
          <p className="text-muted-foreground -mt-4">
            Please fill in your firm's details below to get started. All fields are required.
          </p>
        )}

        {/* Account Details — email + HST grouped to signal both are significant changes */}
        <Card className="w-full">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Account Details</CardTitle>
              <Tooltip
                content="Changes to your account email or HST/GST number are significant — email changes require verification and HST/GST changes trigger a notification to the finance team."
                position="top-right"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Account Email */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Account Email (This email address will be used to access the portal and will receive all project-related notifications, including new SoW requests, SoW change requests, and other important updates)</Label>
              </div>
              {pendingEmail ? (
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">
                      Verification email sent to <strong>{pendingEmail}</strong>. Please check your inbox and click the verification link.
                    </p>
                  </div>
                  <Button
                    buttonText={emailUpdateInProgress ? "Sending..." : "Resend Verification Email"}
                    buttonType="secondary"
                    actionHandler={handleResendVerification}
                    disabled={emailUpdateInProgress}
                  />
                  <p className="text-sm text-muted-foreground">
                    Current email: {userEmail}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{userEmail}</Badge>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account-email-input">Update Account Email</Label>
                    <div className="flex gap-3 items-start">
                      <div className="space-y-1 max-w-sm w-full">
                        <Input
                          id="account-email-input"
                          type="email"
                          value={newEmail}
                          onChange={(e) => { setNewEmail(e.target.value); setEmailError(""); }}
                          placeholder="Enter new email address"
                          className={emailError ? "border-destructive" : ""}
                        />
                        {emailError && <p className="text-xs text-destructive">{emailError}</p>}
                      </div>
                      <Button
                        buttonText={emailUpdateInProgress ? "Updating..." : "Update Email"}
                        buttonType="secondary"
                        actionHandler={handleEmailUpdate}
                        disabled={emailUpdateInProgress || !newEmail}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <hr className="border-border" />

            {/* Tax Numbers (HST/GST + optional PST/RST for split-tax regions) */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Label htmlFor="hst-number-input" className="text-sm font-medium">GST/HST Number</Label>
                  <Tooltip
                    content="Your 15-character CRA GST/HST account number: 9-digit Business Number + RT + 4-digit reference (e.g., 123456789RT0001). Changes will notify the finance team."
                    position="top-right"
                  />
                </div>
                <div className="space-y-1 max-w-sm w-full">
                  <Input
                    id="hst-number-input"
                    type="text"
                    value={hstNumber}
                    onChange={(e) => { setHstNumber(e.target.value); setHstError(""); }}
                    placeholder="e.g., 123456789RT0001"
                    className={hstError ? "border-destructive" : ""}
                  />
                  {hstError && <p className="text-xs text-destructive">{hstError}</p>}
                </div>
              </div>

              {showPstField && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Label htmlFor="pst-number-input" className="text-sm font-medium">PST/RST Number</Label>
                    <Tooltip
                      content="Your provincial sales tax registration number (Manitoba RST: 7 digits, format xxxxxx-x). Required to issue compliant invoices in this region. Changes will notify the finance team."
                      position="top-right"
                    />
                  </div>
                  <div className="space-y-1 max-w-sm w-full">
                    <Input
                      id="pst-number-input"
                      type="text"
                      value={pstNumber}
                      onChange={(e) => { setPstNumber(e.target.value); setPstError(""); }}
                      placeholder="e.g., 123456-7"
                      className={pstError ? "border-destructive" : ""}
                    />
                    {pstError && <p className="text-xs text-destructive">{pstError}</p>}
                  </div>
                </div>
              )}

              <div>
                <Button
                  buttonText={updateFirmProfileMutation.isPending ? "Updating..." : "Update Tax Numbers"}
                  buttonType="secondary"
                  actionHandler={handleTaxNumbersUpdate}
                  disabled={
                    updateFirmProfileMutation.isPending ||
                    (hstNumber === (firm.hstNumber || "") &&
                     (!showPstField || pstNumber === (firm.pstNumber || "")))
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="w-full">
          <Form
            formData={null}
            formDefinition={firmProfileFormDefinition}
            onFormDataChange={handleFormDataChange}
            disabled={false}
            primaryButtonProp={{ buttonText: "", actionHandler: handleSave }}
            hideButtons
            submitRef={submitRef}
          />
        </div>
      </DashboardPageLayout>
    </>
  );
}

export default FirmProfile;
